import { useState } from 'react';
import { Link } from 'react-router-dom';
import AuthShell, { fieldClass } from '../components/AuthShell.js';
import { Button } from '../components/ui.js';
import { useT } from '../lib/i18n.js';
import { apiFetch, ApiError } from '../lib/api.js';

/**
 * Asks for the link that sets a password.
 *
 * One screen for "I have never signed in" and "I forgot it", because from
 * here they are the same request. The answer is the same either way, and
 * deliberately so: telling somebody whether an address is registered would
 * hand them the staff list one guess at a time.
 */
export default function PasswordPage() {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { t } = useT();

  const request = async () => {
    setBusy(true);
    setError(null);
    try {
      await apiFetch('/api/auth/password/request', {
        method: 'POST',
        body: { email },
        anonymous: true,
      });
      setSent(true);
    } catch (err) {
      setError(
        err instanceof ApiError && err.code === 'MAINTENANCE'
          ? 'Password links are not set up on this instance yet. Ask your administrator.'
          : 'Could not send the link. Try again shortly.',
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell
      title={t('Set your password')}
      subtitle={t('We send a link to your work email. Following it proves the address is yours, and then you choose a password.')}
      footer={
        <Link to="/signin" className="font-extrabold text-brand-ink">
          {t('Back to sign in')}
        </Link>
      }
    >
      <form
        className="flex flex-col"
        onSubmit={(e) => {
          e.preventDefault();
          void request();
        }}
      >
        <label
          htmlFor="email"
          className="mb-2 block text-[13px] font-extrabold text-muted md:text-[14.5px]"
        >
          {t('Work email')}
        </label>
        <input
          id="email"
          type="email"
          inputMode="email"
          autoComplete="username"
          placeholder="you@kokomo-plant.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className={fieldClass}
        />

        <Button type="submit" disabled={busy || !email.includes('@')}>
          {busy ? `${t('Send the link')}…` : sent ? t('Send another link') : t('Send the link')}
        </Button>

        {sent && !error && (
          <p className="mt-3.5 rounded-tile bg-ok-soft px-4 py-3.5 text-[13.5px] text-ok-ink md:text-[15px]">
            If {email} is registered, a link is on its way. It expires in 15 minutes — after you
            set a password you will not need one again.
          </p>
        )}

        {error && (
          <p className="mt-3.5 rounded-tile bg-danger-soft px-4 py-3.5 text-[13.5px] font-bold text-danger md:text-[15px]">
            {error}
          </p>
        )}
      </form>
    </AuthShell>
  );
}
